class MainFaqs extends HTMLElement {
    constructor() {
        super();
        this.sidebarLinks = this.querySelectorAll('.faq-sidebar-link');
        this.faqSections = this.querySelectorAll('[data-faq-section]');
        this.HEADER_OFFSET = 50;
    }

    connectedCallback() {
        /* Accordion init (grouped per FAQ category, so opening one closes the rest in its group) */
        this.initAccordionGroups();

        /* Sidebar scroll */
        this.initSidebarScroll();

        /* Active state observer */
        this.initActiveObserver();
        this.initSeeAllFaqs();
    }

    initAccordionGroups() {
        const categories = this.querySelectorAll(".faq-category");

        if (!categories.length) {
            /* Fallback: no categories present, treat every accordion on the page as one group */
            const allDetails = Array.from(this.querySelectorAll('.main-faqs-details'));
            allDetails.forEach((el) => new ProductFaqItem(el, allDetails));
            return;
        }

        categories.forEach(category => {
            const groupItems = [];

            let next = category.nextElementSibling;

            while (next && !next.classList.contains("faq-category")) {
                const details = next.querySelector('.main-faqs-details');
                if (details) groupItems.push(details);
                next = next.nextElementSibling;
            }

            groupItems.forEach((el) => new ProductFaqItem(el, groupItems));
        });
    }


    initSeeAllFaqs() {
        /*
            Liquid already decides, per category:
            - whether the "See All" button renders at all (only if FAQ count > visible_faqs_count)
            - which FAQ blocks start with the "hidden" class

            JS just needs to wire up the click: reveal the hidden blocks in that
            category and hide the button.
        */
        const categories = this.querySelectorAll(".faq-category");

        categories.forEach(category => {

            let faqBlocks = [];
            let btnContainer = null;

            let next = category.nextElementSibling;

            while (next && !next.classList.contains("faq-category")) {

                if (next.querySelector(".section-main-faqs-block")) {
                    faqBlocks.push(next);
                }

                if (next.querySelector(".faq-see-all-btn")) {
                    btnContainer = next;
                }

                next = next.nextElementSibling;
            }

            if (!btnContainer) return;

            const btn = btnContainer.querySelector(".faq-see-all-btn");

            btn.addEventListener("click", (e) => {
                e.preventDefault();

                faqBlocks.forEach(faq => {
                    faq.classList.remove("hidden");
                });

                btn.classList.add("hidden");
            });

        });
    }

    initSidebarScroll() {
        this.sidebarLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();

                const target = this.querySelector(link.getAttribute('href'));
                if (!target) return;

                const offsetTop =
                    target.getBoundingClientRect().top +
                    window.pageYOffset -
                    this.HEADER_OFFSET;

                window.scrollTo({
                    top: offsetTop,
                    behavior: 'smooth'
                });
            });
        });
    }

    initActiveObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;

                    const id = entry.target.id;

                    this.sidebarLinks.forEach(link => {
                        link.classList.toggle(
                            'active',
                            link.getAttribute('href') === `#${id}`
                        );
                    });
                });
            },
            {
                rootMargin: '-30% 0px -60% 0px',
                threshold: 0
            }
        );

        this.faqSections.forEach(section => observer.observe(section));
    }
}

/* ===============================
   ACCORDION ITEM
================================ */

class ProductFaqItem {
    constructor(el, group = [el]) {
        this.el = el;
        this.group = group;
        this.summary = el.querySelector('summary');
        this.content = el.querySelector('.content');
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        /* Store the instance on the element so group-mates can reach it directly */
        this.el._faqItemInstance = this;

        this.summary.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(e) {
        e.preventDefault();

        if (this.isClosing || !this.el.open) {
            this.closeOthersInGroup();
            this.open();
        } else {
            this.shrink();
        }
    }

    closeOthersInGroup() {
        this.group.forEach((otherEl) => {
            if (otherEl === this.el) return;

            const instance = otherEl._faqItemInstance;
            if (!instance) return;

            if (otherEl.open || instance.isExpanding) {
                instance.shrink();
            }
        });
    }

    shrink() {
        this.el.style.overflow = 'hidden';
        this.isClosing = true;

        const startHeight = `${this.el.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight}px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.el.animate(
            { height: [startHeight, endHeight] },
            { duration: 400, easing: 'ease-out' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(false);
        this.animation.oncancel = () => (this.isClosing = false);
    }

    open() {
        this.el.style.overflow = 'hidden';
        this.el.style.height = `${this.el.offsetHeight}px`;
        this.el.open = true;

        requestAnimationFrame(() => this.expand());
    }

    expand() {
        this.isExpanding = true;

        const startHeight = `${this.el.offsetHeight}px`;
        const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

        if (this.animation) this.animation.cancel();

        this.animation = this.el.animate(
            { height: [startHeight, endHeight] },
            { duration: 400, easing: 'ease-out' }
        );

        this.animation.onfinish = () => this.onAnimationFinish(true);
        this.animation.oncancel = () => (this.isExpanding = false);
    }

    onAnimationFinish(open) {
        this.el.open = open;
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;
        this.el.style.height = '';
        this.el.style.overflow = '';
    }
}

customElements.define('main-faqs', MainFaqs);